<script lang="ts" module>
  // GRID is the faint gridline color, a tint of the text so it reads the
  // same on a card, a tinted Health card, and in either theme.
  export const GRID = 'color-mix(in srgb, var(--theme-text) 13%, transparent)';

  // measure gives a label's width in the font it is drawn in, so marks that
  // would overlap are left out at a narrow width or with large text.
  let ctx: CanvasRenderingContext2D | null = null;
  function measure(text: string, font: string): number {
    ctx ??= document.createElement('canvas').getContext('2d');
    if (!ctx) return text.length * 7;
    ctx.font = font;
    return ctx.measureText(text).width;
  }
</script>

<script lang="ts">
  import type { Snippet } from 'svelte';
  import { yPct } from '../../utils/chart';

  // Chart is the frame every chart on the web draws in (spec 010
  // 010 FR-033): the value scale's round values on the right with its unit
  // above them, marks along the bottom, and faint gridlines at both. plot
  // draws in SVG in a box 0 to 100 each way, stretched to the frame, and
  // overlay in HTML over it for bars and points that must keep their shape.
  //
  // It is also the one way to inspect a chart: hovering places the guide at
  // the nearest of positions, and tapping, dragging, or the arrow keys
  // select one, which stays until Escape. hover and at are bindable so
  // stacked charts share one guide and the readout above them follows it.
  interface Props {
    lo: number;
    hi: number;
    yTicks?: { v: number; text: string }[];
    unit?: string;
    xTicks?: { at: number; text: string }[];
    // xLabels false keeps the vertical gridlines but leaves the marks'
    // words to the chart below it in a stack; xGrid false leaves out the
    // vertical gridlines where each bar is named under it.
    xLabels?: boolean;
    xGrid?: boolean;
    height?: string;
    positions?: number[];
    at?: number | null;
    hover?: number | null;
    step?: number;
    label: string;
    valueText?: string;
    onkey?: (e: KeyboardEvent) => boolean;
    // dots mark each series' value at the guide.
    dots?: { v: number; color: string }[];
    plot?: Snippet;
    overlay?: Snippet;
  }
  let {
    lo, hi, yTicks = [], unit, xTicks = [], xLabels = true, xGrid = true, height = 'h-32', positions = [],
    at = $bindable(null), hover = $bindable(null), step = 1, label, valueText, onkey, dots = [], plot, overlay,
  }: Props = $props();

  let root: HTMLElement | undefined = $state();
  let w = $state(0);
  let font = $state('12px sans-serif');
  let pressed = false;

  $effect(() => {
    if (root) font = getComputedStyle(root).font;
  });

  let gutter = $derived(yTicks.length ? Math.ceil(Math.max(...yTicks.map((t) => measure(t.text, font)))) + 8 : 0);

  // shownX are the marks whose words fit, each clamped inside the plot and
  // left out when it would overlap the one before.
  let shownX = $derived.by(() => {
    const out: { left: number; text: string }[] = [];
    let end = -Infinity;
    for (const t of xTicks) {
      const tw = measure(t.text, font);
      const left = Math.max(0, Math.min(w - tw, t.at * w - tw / 2));
      if (left < end + 8) continue;
      out.push({ left, text: t.text });
      end = left + tw;
    }
    return out;
  });

  let shown = $derived(hover ?? at);
  let guide = $derived(shown != null && positions[shown] != null ? positions[shown] : null);

  function nearest(f: number): number {
    let best = 0;
    for (let i = 1; i < positions.length; i++) {
      if (Math.abs(positions[i] - f) < Math.abs(positions[best] - f)) best = i;
    }
    return best;
  }

  function place(e: PointerEvent): number {
    const box = (e.currentTarget as HTMLElement).getBoundingClientRect();
    return nearest(Math.max(0, Math.min(1, (e.clientX - box.left) / box.width)));
  }

  function down(e: PointerEvent) {
    if (!positions.length) return;
    (e.currentTarget as HTMLElement).setPointerCapture(e.pointerId);
    pressed = true;
    at = place(e);
    hover = null;
  }

  function move(e: PointerEvent) {
    if (!positions.length) return;
    if (pressed) at = place(e);
    else if (e.pointerType === 'mouse') hover = place(e);
  }

  function key(e: KeyboardEvent) {
    if (!positions.length) return;
    if (onkey?.(e)) {
      e.preventDefault();
      return;
    }
    const n = positions.length;
    const cur = at ?? hover;
    hover = null;
    if (e.key === 'ArrowRight') at = cur == null ? 0 : Math.min(n - 1, cur + step);
    else if (e.key === 'ArrowLeft') at = cur == null ? n - 1 : Math.max(0, cur - step);
    else if (e.key === 'Home') at = 0;
    else if (e.key === 'End') at = n - 1;
    else if (e.key === 'Escape') at = null;
    else return;
    e.preventDefault();
  }
</script>

<div bind:this={root} class="text-xs text-text-muted tabular-nums select-none">
  {#if unit}
    <p class="text-right leading-none mb-1.5" aria-hidden="true">{unit}</p>
  {/if}
  <div class="flex {unit ? '' : 'pt-1.5'}">
    <!-- The role is a slider whenever the frame takes a tab stop; svelte-check cannot follow the shared condition. -->
    <!-- svelte-ignore a11y_no_noninteractive_tabindex -->
    <div bind:clientWidth={w} class="relative flex-1 min-w-0 {height} rounded-sm outline-none focus-visible:ring-2 {positions.length ? 'cursor-crosshair' : ''}"
      style="touch-action: pan-y; --tw-ring-color: var(--theme-focus-ring-color);"
      role={positions.length ? 'slider' : 'img'} tabindex={positions.length ? 0 : undefined} aria-label={label}
      aria-valuemin={positions.length ? 0 : undefined} aria-valuemax={positions.length ? positions.length - 1 : undefined}
      aria-valuenow={positions.length ? (shown ?? 0) : undefined} aria-valuetext={positions.length ? valueText : undefined}
      onpointerdown={down} onpointermove={move} onpointerup={() => (pressed = false)} onpointercancel={() => (pressed = false)}
      onpointerleave={(e) => { if (e.pointerType === 'mouse') hover = null; }} onkeydown={key}>
      <svg viewBox="0 0 100 100" class="absolute inset-0 w-full h-full overflow-visible" preserveAspectRatio="none" aria-hidden="true">
        {#each yTicks as t (t.v)}
          <line x1="0" x2="100" y1={yPct(t.v, lo, hi)} y2={yPct(t.v, lo, hi)} stroke={GRID} stroke-width="1" vector-effect="non-scaling-stroke" />
        {/each}
        {#each xGrid ? xTicks : [] as t (t.at)}
          <line x1={t.at * 100} x2={t.at * 100} y1="0" y2="100" stroke={GRID} stroke-width="1" vector-effect="non-scaling-stroke" />
        {/each}
        {@render plot?.()}
      </svg>
      {@render overlay?.()}
      {#if guide != null}
        <span class="absolute inset-y-0 w-px pointer-events-none" style="left: {guide * 100}%; background: var(--theme-text);" aria-hidden="true"></span>
        {#each dots as d, k (k)}
          <span class="absolute w-2.5 h-2.5 rounded-full pointer-events-none" aria-hidden="true"
            style="left: calc({guide * 100}% - 5px); top: calc({yPct(d.v, lo, hi)}% - 5px); background: {d.color}; box-shadow: 0 0 0 2px var(--theme-card-bg);"></span>
        {/each}
      {/if}
    </div>
    {#if gutter}
      <div class="relative shrink-0" style="width: {gutter}px;" aria-hidden="true">
        {#each yTicks as t (t.v)}
          <span class="absolute right-0 leading-none whitespace-nowrap" style="top: {yPct(t.v, lo, hi)}%; transform: translateY(-50%);">{t.text}</span>
        {/each}
      </div>
    {/if}
  </div>
  {#if xLabels && xTicks.length}
    <div class="relative h-4 mt-1" style="margin-right: {gutter}px;" aria-hidden="true">
      {#each shownX as t (t.left)}
        <span class="absolute top-0 whitespace-nowrap leading-4" style="left: {t.left}px;">{t.text}</span>
      {/each}
    </div>
  {/if}
</div>
